import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { axiosInstance } from '../../App';
import { ProductSuggestInput } from '../PriceShopping';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Tag, Plus, Loader2, Trash2, Pencil, X, Eye, MousePointerClick, ImagePlus, StopCircle, CreditCard, CalendarPlus, Rocket } from 'lucide-react';

const money = (v, ccy) => (v == null ? null : `${ccy || 'EUR'} ${Number(v).toFixed(2)}`);
const STATUS_STYLE = {
  live: 'bg-emerald-100 text-emerald-700',
  scheduled: 'bg-sky-100 text-sky-700',
  pending: 'bg-amber-100 text-amber-700',
  suspended: 'bg-rose-100 text-rose-700',
  expired: 'bg-gray-200 text-gray-600',
  archived: 'bg-gray-200 text-gray-500',
};

const emptyForm = (town) => ({
  productName: '', normalizedName: null, brand: '', packSize: '',
  normalPrice: '', promoPrice: '', description: '', photoUrl: '',
  town: town || '', shopBranch: '', startDate: '', durationDays: null, availabilityText: '',
});

const centsToStr = (c, ccy) => `${ccy || 'EUR'} ${(Number(c || 0) / 100).toFixed(2)}`;
const addDays = (iso, days) => { const d = iso ? new Date(iso) : new Date(); d.setDate(d.getDate() + Number(days)); return d.toISOString().slice(0, 10); };

export const PricePromotionsSection = ({ business, embedded = false }) => {
  const { t } = useTranslation();
  const [allowance, setAllowance] = useState(null);
  const [settings, setSettings] = useState(null);
  const [promos, setPromos] = useState([]);
  const [view, setView] = useState('live'); // 'live' | 'scheduled' | 'expired'
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm(business?.city));
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [a, m, s] = await Promise.all([
        axiosInstance.get('/price/promotions/allowance'),
        axiosInstance.get('/price/promotions/mine', { params: { view } }),
        axiosInstance.get('/price/promotions/settings'),
      ]);
      setAllowance(a.data);
      setPromos(m.data?.promotions || []);
      setSettings(s.data);
    } catch (e) {
      // silent — section simply shows nothing if it errors
    } finally { setLoading(false); }
  }, [view]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const openCreate = () => {
    setEditingId(null);
    const def = settings?.durations?.[0]?.days ?? 7;
    setForm({ ...emptyForm(business?.city), durationDays: def });
    setShowForm(true);
  };
  const openEdit = (p) => {
    setEditingId(p.id);
    setForm({
      productName: p.productName || '', normalizedName: p.normalizedName || null,
      brand: p.brand || '', packSize: p.packSize || '',
      normalPrice: p.normalPrice ?? '', promoPrice: p.promoPrice ?? '',
      description: p.description || '', photoUrl: p.photoUrl || '',
      town: p.town || '', shopBranch: p.shopBranch || '', startDate: (p.startDate || '').slice(0, 10),
      durationDays: p.durationDays || null, availabilityText: p.availabilityText || '',
    });
    setShowForm(true);
  };

  // Boost Again — reuse the EXISTING create flow, pre-filled from an expired/
  // ended promotion. editingId stays null so this is a brand-new promotion
  // (new period + fresh stats); the original record is never touched. Duration
  // is left unselected so the business must choose + confirm before publishing.
  const boostAgain = (p) => {
    setEditingId(null);
    setForm({
      productName: p.productName || '', normalizedName: p.normalizedName || null,
      brand: p.brand || '', packSize: p.packSize || '',
      normalPrice: p.normalPrice ?? '', promoPrice: p.promoPrice ?? '',
      description: p.description || '', photoUrl: p.photoUrl || '',
      town: p.town || business?.city || '', shopBranch: p.shopBranch || '',
      startDate: '', durationDays: null, availabilityText: p.availabilityText || '',
    });
    setShowForm(true);
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axiosInstance.post('/business/offer/upload-image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm((f) => ({ ...f, photoUrl: res.data?.url || '' }));
    } catch (e) {
      toast.error(t('pricePromo.photoError', 'Could not upload photo'));
    } finally { setUploading(false); }
  };

  const submit = async () => {
    if (!form.productName.trim()) { toast.error(t('pricePromo.needProduct', 'Please enter a product name')); return; }
    if (!form.promoPrice || Number(form.promoPrice) <= 0) { toast.error(t('pricePromo.needPromoPrice', 'Please enter a promotional price')); return; }
    if (!editingId && !form.durationDays) { toast.error(t('pricePromo.needDuration', 'Please choose a promotion duration')); return; }
    setSubmitting(true);
    const base = {
      productName: form.productName.trim(),
      normalizedName: form.normalizedName || null,
      brand: form.brand.trim() || null,
      packSize: form.packSize.trim() || null,
      normalPrice: form.normalPrice !== '' ? Number(form.normalPrice) : null,
      promoPrice: Number(form.promoPrice),
      description: form.description.trim() || null,
      photoUrl: form.photoUrl || null,
      town: form.town.trim() || null,
      shopBranch: form.shopBranch.trim() || null,
      availabilityText: form.availabilityText.trim() || null,
    };
    try {
      if (editingId) {
        await axiosInstance.put(`/price/promotions/${editingId}`, { ...base, startDate: form.startDate || null });
        toast.success(t('pricePromo.updated', 'Promotion updated'));
      } else {
        const res = await axiosInstance.post('/price/promotions', { ...base, startDate: form.startDate || null, durationDays: Number(form.durationDays) });
        if (res.data?.freeIntro) toast.success(t('pricePromo.publishedFree', 'Your FREE promotion is now live'));
        else toast.success(res.data?.published ? t('pricePromo.published', 'Promotion is now live') : t('pricePromo.submitted', 'Promotion submitted for review'));
      }
      setShowForm(false); setEditingId(null); loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('pricePromo.saveError', 'Could not save promotion'));
    } finally { setSubmitting(false); }
  };

  const endEarly = async (id) => {
    try { await axiosInstance.post(`/price/promotions/${id}/end-early`); toast.success(t('pricePromo.ended', 'Promotion ended')); loadAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };

  const payNow = async (id) => {
    try {
      const res = await axiosInstance.post(`/price/promotions/${id}/checkout`, { originUrl: window.location.origin });
      if (res.data?.checkoutUrl) { window.location.href = res.data.checkoutUrl; return; }
      toast.success(t('pricePromo.publishedFree', 'Promotion is now live'));
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };

  const extend = async (id) => {
    const opts = (settings?.durations || []).map((d) => d.days);
    const pick = window.prompt(t('pricePromo.extendPrompt', 'Extend by how many days?') + ' (' + opts.join(' / ') + ')', String(opts[0] || 7));
    if (pick == null) return;
    try {
      const res = await axiosInstance.post(`/price/promotions/${id}/extend-checkout`, { days: Number(pick), originUrl: window.location.origin });
      if (res.data?.checkoutUrl) { window.location.href = res.data.checkoutUrl; return; }
      toast.success(t('pricePromo.extended', 'Promotion extended'));
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };

  const archive = async (id) => {
    try { await axiosInstance.post(`/price/promotions/${id}/archive`); toast.success(t('pricePromo.archived', 'Promotion archived')); loadAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };

  const canCreate = allowance?.canCreate;

  return (
    <div id="price-promotions-section" className="bg-white rounded-2xl shadow-sm border p-4 sm:p-5 space-y-4" data-testid="price-promotions-section">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <div className="h-9 w-9 rounded-xl bg-indigo-50 flex items-center justify-center shrink-0"><Tag className="h-5 w-5 text-indigo-600" /></div>
          <div className="min-w-0">
            <h3 className="text-base font-bold text-gray-900 truncate">{embedded ? t('pricePromo.embedTitle', 'My Promotions') : t('pricePromo.title', 'Price-2-Day Promotions')}</h3>
            <p className="text-[12px] text-gray-500">{t('pricePromo.subtitle', 'Promote a product offer to nearby shoppers')}</p>
          </div>
        </div>
        {allowance && (
          <span className="shrink-0 text-[11px] px-2.5 py-1 rounded-full bg-gray-100 text-gray-600" data-testid="promo-allowance">
            {t('pricePromo.slots', 'Slots')}: {allowance.remaining}/{allowance.allowance}
          </span>
        )}
      </div>

      {allowance?.suspended && (
        <p className="text-[12px] bg-rose-50 text-rose-700 rounded-lg px-3 py-2" data-testid="promo-suspended-note">
          {t('pricePromo.suspendedNote', 'Your business is currently suspended from promotions. Please contact support.')}
        </p>
      )}

      {!allowance?.suspended && allowance && (
        allowance.introAvailable ? (
          <p className="text-[12px] bg-emerald-50 text-emerald-700 rounded-lg px-3 py-2" data-testid="promo-intro-available">
            🎁 {t('pricePromo.introAvailable', 'Your first 7-day promotion is FREE (one per business).')}
          </p>
        ) : (
          <p className="text-[12px] text-gray-400" data-testid="promo-intro-used">
            {t('pricePromo.introUsed', 'Free introductory promotion used.')}
          </p>
        )
      )}

      {!showForm && (
        <Button onClick={openCreate} disabled={!canCreate} className="rounded-full bg-indigo-600 hover:bg-indigo-700" data-testid="promo-new-button">
          <Plus className="h-4 w-4 mr-1" />{embedded ? t('pricePromo.promoteItem', 'Promote an Item') : t('pricePromo.new', 'New promotion')}
        </Button>
      )}
      {!showForm && !canCreate && !allowance?.suspended && allowance && (
        <p className="text-[12px] text-gray-400" data-testid="promo-no-slots">{t('pricePromo.noSlots', 'No promotion slots remaining. Archive an active one to free a slot.')}</p>
      )}

      {/* Create / edit form */}
      {showForm && (
        <div className="rounded-xl border bg-gray-50 p-3 space-y-3" data-testid="promo-form">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-gray-800">{editingId ? t('pricePromo.editTitle', 'Edit promotion') : t('pricePromo.createTitle', 'Create promotion')}</p>
            <button onClick={() => { setShowForm(false); setEditingId(null); }} className="text-gray-400" data-testid="promo-form-close"><X className="h-4 w-4" /></button>
          </div>

          <div>
            <label className="text-[11px] text-gray-500">{t('pricePromo.product', 'Product')}</label>
            <ProductSuggestInput
              value={form.productName}
              onChange={(v) => setForm((f) => ({ ...f, productName: v, normalizedName: null }))}
              onSelect={(s) => setForm((f) => ({ ...f, productName: s.name, normalizedName: s.normalizedName || null, brand: s.brand || f.brand, packSize: s.packSize || f.packSize }))}
              placeholder={t('pricePromo.productPh', 'e.g. Olive Oil 2L')}
              testid="promo-product-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[11px] text-gray-500">{t('pricePromo.brand', 'Brand')}</label>
              <Input value={form.brand} onChange={(e) => setForm((f) => ({ ...f, brand: e.target.value }))} className="h-9 text-sm" data-testid="promo-brand-input" />
            </div>
            <div>
              <label className="text-[11px] text-gray-500">{t('pricePromo.pack', 'Pack / size')}</label>
              <Input value={form.packSize} onChange={(e) => setForm((f) => ({ ...f, packSize: e.target.value }))} className="h-9 text-sm" data-testid="promo-pack-input" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[11px] text-gray-500">{t('pricePromo.normalPrice', 'Normal price')} ({t('pricePromo.optional', 'optional')})</label>
              <Input type="number" step="0.01" value={form.normalPrice} onChange={(e) => setForm((f) => ({ ...f, normalPrice: e.target.value }))} className="h-9 text-sm" data-testid="promo-normalprice-input" />
            </div>
            <div>
              <label className="text-[11px] text-gray-500">{t('pricePromo.promoPrice', 'Promotional price')} *</label>
              <Input type="number" step="0.01" value={form.promoPrice} onChange={(e) => setForm((f) => ({ ...f, promoPrice: e.target.value }))} className="h-9 text-sm" data-testid="promo-promoprice-input" />
            </div>
          </div>

          <div>
            <label className="text-[11px] text-gray-500">{t('pricePromo.description', 'Description')}</label>
            <Input value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} className="h-9 text-sm" data-testid="promo-description-input" />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[11px] text-gray-500">{t('pricePromo.town', 'Town / area')}</label>
              <Input value={form.town} onChange={(e) => setForm((f) => ({ ...f, town: e.target.value }))} className="h-9 text-sm" data-testid="promo-town-input" />
            </div>
            <div>
              <label className="text-[11px] text-gray-500">{t('pricePromo.branch', 'Shop / branch')} ({t('pricePromo.optional', 'optional')})</label>
              <Input value={form.shopBranch || ''} onChange={(e) => setForm((f) => ({ ...f, shopBranch: e.target.value }))} className="h-9 text-sm" data-testid="promo-branch-input" />
            </div>
          </div>

          <div>
            <label className="text-[11px] text-gray-500">{t('pricePromo.start', 'Start date')} ({t('pricePromo.startHint', 'leave blank for today')})</label>
            <Input type="date" value={form.startDate} onChange={(e) => setForm((f) => ({ ...f, startDate: e.target.value }))} className="h-9 text-sm" data-testid="promo-start-input" />
          </div>

          {!editingId && (
            <div>
              <label className="text-[11px] text-gray-500">{t('pricePromo.duration', 'Promotion duration')} *</label>
              <div className="grid grid-cols-3 gap-2 mt-1" data-testid="promo-duration-options">
                {(settings?.durations || []).map((d) => {
                  const isIntroFree = allowance?.introAvailable && settings?.introFreeEnabled && d.days === settings?.introFreeDurationDays;
                  const selected = Number(form.durationDays) === d.days;
                  return (
                    <button
                      key={d.days}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, durationDays: d.days }))}
                      className={`rounded-xl border p-2 text-center transition-colors ${selected ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 bg-white'}`}
                      data-testid={`promo-duration-${d.days}`}
                    >
                      <div className="text-sm font-bold text-gray-900">{d.days} {t('pricePromo.days', 'days')}</div>
                      {isIntroFree
                        ? <div className="text-[11px] font-bold text-emerald-600" data-testid="promo-duration-free">{t('pricePromo.free', 'FREE')}</div>
                        : <div className="text-[11px] text-gray-500">{centsToStr(d.priceCents, settings?.currency)}</div>}
                    </button>
                  );
                })}
              </div>
              {form.durationDays && (
                <p className="text-[11px] text-gray-400 mt-1" data-testid="promo-computed-end">
                  {t('pricePromo.willEnd', 'Ends')} {addDays(form.startDate || null, form.durationDays)}
                </p>
              )}
              {form.durationDays && !( allowance?.introAvailable && form.durationDays === settings?.introFreeDurationDays ) && !settings?.paidPromotionsEnabled && (
                <p className="text-[11px] text-amber-600 mt-1" data-testid="promo-testmode-note">{t('pricePromo.testMode', 'Preview test mode — price shown, no real charge is made')}</p>
              )}
            </div>
          )}

          <div>
            <label className="text-[11px] text-gray-500">{t('pricePromo.availability', 'Availability wording')} ({t('pricePromo.optional', 'optional')})</label>
            <Input value={form.availabilityText} onChange={(e) => setForm((f) => ({ ...f, availabilityText: e.target.value }))} className="h-9 text-sm" placeholder={t('pricePromo.availabilityPh', 'e.g. While stocks last')} data-testid="promo-availability-input" />
          </div>

          <div className="flex items-center gap-3">
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(e.target.files?.[0])} data-testid="promo-photo-file" />
            <Button type="button" variant="outline" className="rounded-full h-9 text-sm" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="promo-photo-button">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4 mr-1" />}{t('pricePromo.photo', 'Photo')} ({t('pricePromo.optional', 'optional')})
            </Button>
            {form.photoUrl && <img src={form.photoUrl} alt="" className="h-10 w-10 rounded object-cover" data-testid="promo-photo-preview" />}
          </div>

          <div className="flex gap-2 pt-1">
            <Button onClick={submit} disabled={submitting} className="rounded-full bg-indigo-600 hover:bg-indigo-700 flex-1" data-testid="promo-submit-button">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : (editingId ? t('pricePromo.save', 'Save') : t('pricePromo.publish', 'Publish'))}
            </Button>
          </div>
        </div>
      )}

      {/* view toggle */}
      <div className="flex gap-2 text-[12px]">
        <button onClick={() => setView('live')} className={`px-2.5 py-1 rounded-full ${view === 'live' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600'}`} data-testid="promo-view-live">{t('pricePromo.viewLive', 'Live')}</button>
        <button onClick={() => setView('scheduled')} className={`px-2.5 py-1 rounded-full ${view === 'scheduled' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600'}`} data-testid="promo-view-scheduled">{t('pricePromo.viewScheduled', 'Scheduled')}</button>
        <button onClick={() => setView('expired')} className={`px-2.5 py-1 rounded-full ${view === 'expired' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600'}`} data-testid="promo-view-expired">{t('pricePromo.viewExpired', 'Expired')}</button>
      </div>

      {loading ? (
        <div className="py-6 text-center"><Loader2 className="h-5 w-5 animate-spin text-indigo-500 mx-auto" /></div>
      ) : promos.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-4" data-testid="promo-empty">{t('pricePromo.empty', 'No promotions here yet.')}</p>
      ) : (
        <div className="space-y-2" data-testid="promo-list">
          {promos.map((p) => (
            <div key={p.id} className="rounded-xl border p-3" data-testid={`promo-item-${p.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{p.productName}</p>
                  <p className="text-[12px] text-gray-500">
                    <span className="font-bold text-indigo-700">{money(p.promoPrice, p.currency)}</span>
                    {p.normalPrice != null && <span className="line-through ml-2 text-gray-400">{money(p.normalPrice, p.currency)}</span>}
                    {p.packSize ? ` · ${p.packSize}` : ''}
                  </p>
                  <p className="text-[11px] text-gray-400">{p.town || '—'} · {p.durationDays ? `${p.durationDays} ${t('pricePromo.days', 'days')} · ` : ''}{t('pricePromo.ends', 'ends')} {(p.endDate || '').slice(0, 10)}</p>
                  <div className="flex items-center gap-3 mt-1 text-[11px] text-gray-500">
                    <span className="inline-flex items-center gap-1" data-testid={`promo-views-${p.id}`}><Eye className="h-3 w-3" />{p.views ?? 0}</span>
                    <span className="inline-flex items-center gap-1" data-testid={`promo-clicks-${p.id}`}><MousePointerClick className="h-3 w-3" />{p.clicks ?? 0}</span>
                    {p.isFreeIntro && <span className="text-emerald-600 font-semibold">{t('pricePromo.free', 'FREE')}</span>}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2 shrink-0">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${STATUS_STYLE[p.statusLabel] || 'bg-gray-100 text-gray-500'}`} data-testid={`promo-status-${p.id}`}>{t(`pricePromo.status.${p.statusLabel}`, p.statusLabel)}</span>
                  <div className="flex gap-1">
                    {p.statusLabel !== 'archived' && (
                      <button onClick={() => openEdit(p)} className="p-1.5 rounded-lg bg-gray-100 text-gray-600" title={t('pricePromo.edit', 'Edit')} data-testid={`promo-edit-${p.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                    )}
                    {p.paymentStatus === 'pending_payment' && (
                      <button onClick={() => payNow(p.id)} className="p-1.5 rounded-lg bg-emerald-600 text-white" title={t('pricePromo.payNow', 'Pay now')} data-testid={`promo-pay-${p.id}`}><CreditCard className="h-3.5 w-3.5" /></button>
                    )}
                    {(p.statusLabel === 'live' || p.statusLabel === 'scheduled') && (
                      <button onClick={() => extend(p.id)} className="p-1.5 rounded-lg bg-sky-50 text-sky-700" title={t('pricePromo.extend', 'Extend')} data-testid={`promo-extend-${p.id}`}><CalendarPlus className="h-3.5 w-3.5" /></button>
                    )}
                    {(p.statusLabel === 'live' || p.statusLabel === 'scheduled') && (
                      <button onClick={() => endEarly(p.id)} className="p-1.5 rounded-lg bg-amber-50 text-amber-700" title={t('pricePromo.endEarly', 'End early')} data-testid={`promo-endearly-${p.id}`}><StopCircle className="h-3.5 w-3.5" /></button>
                    )}
                    {(p.statusLabel === 'expired' || p.statusLabel === 'archived') && canCreate && (
                      <button onClick={() => boostAgain(p)} className="px-2 py-1 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-[11px] font-semibold inline-flex items-center gap-1" title={t('pricePromo.boostAgain', 'Boost Again')} data-testid={`promo-boost-${p.id}`}><Rocket className="h-3.5 w-3.5" />{t('pricePromo.boostAgain', 'Boost Again')}</button>
                    )}
                    {p.statusLabel !== 'archived' && (
                      <button onClick={() => archive(p.id)} className="p-1.5 rounded-lg bg-rose-50 text-rose-600" title={t('pricePromo.archive', 'Archive')} data-testid={`promo-archive-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PricePromotionsSection;
